import request from '@/request'
import { ACCESS, FUSEBOX, PUBLIC } from '@/data/api'
import { bindVar, objQueryString } from '@/util'
import ability from '@/store/ability'

export default {
  namespaced: true,
  state: {
    single: {
      labels: [],
      images: []
    },
    fuseboxList: [],
    history: [],
    fuseboxGroupList: [],
    limit: 3,
    pagination: {
      page: 1,
      lastPage: 0,
      total: 0
    },
    search: '',
    label: '',
    shared: false
  },
  getters: {
    single: state => state.single,
    history: state => state.history,
    fuseboxList: state => state.fuseboxList,
    fuseboxGroupList: state => state.fuseboxGroupList,
    lastPage: state => state.pagination.lastPage,
    currentPage: state => state.pagination.page,
    total: state => state.pagination.total,
    search: state => state.search,
    label: state => state.label,
    isFilterApplied: (state) => {
      return !!state.search || state.shared
    },
    shared: state => state.shared
  },
  mutations: {
    clearFusebox (state) {
      state.single = {
        labels: [],
        images: []
      }
      state.fuseboxGroupList = []
      state.history = []
    },
    setSingle (state, fusebox) {
      state.single = fusebox
    },
    setHistory (state, history) {
      state.history = history
    },
    changeSingleDate (state, dt) {
      state.single.last_modified_at = dt
    },
    setFuseboxList (state, items) {
      state.fuseboxList = items
    },
    setFuseboxGroupList (state, items) {
      state.fuseboxGroupList = items
    },
    setPage (state, page) {
      state.pagination.page = page
    },
    setLastPage (state, page) {
      state.pagination.lastPage = page
    },
    setTotal (state, count) {
      state.pagination.total = count
    },
    setSearch (state, search) {
      state.search = search
    },
    setLabel (state, label) {
      state.label = label
    },
    setShared (state, shared) {
      state.shared = shared
    }
  },
  actions: {
    async get ({ commit, getters }, params) {
      try {
        const queryParams = {
          page: getters.currentPage,
          keyword: getters.search,
          label: getters.label
        }
        if (ability.can('request_access', 'plans')) {
          queryParams['shared'] = getters.shared
        }

        const { data } = await request.get(`${FUSEBOX.RESOURCE}?${objQueryString(queryParams)}`)
        commit('setFuseboxList', data.data)
        commit('setLastPage', data.meta.last_page)
        commit('setTotal', data.meta.total)
      } catch (e) {
        commit('setFuseboxList', [])
        commit('setLastPage', 0)
        commit('setTotal', 0)
      }
    },
    resetPage ({ commit }, payload) {
      if (payload.resetPage) {
        commit('setPage', 1)
      }
    },
    resetFilters ({ commit }) {
      commit('setLabel', '')
      commit('setSearch', '')
      commit('setShared', false)
    },
    async init ({ commit, getters }) {
      try {
        const { data } = await request.get(`${FUSEBOX.INIT}`)
        return data
      } catch (e) {
        commit('setSingle', {})
      }
    },
    async show ({ commit, getters }, params) {
      try {
        const PATH = params.public ? `${bindVar(PUBLIC.RESOURCE, { fusebox: params.id })}` : `${FUSEBOX.RESOURCE}/${params.id}`
        const { data } = await request.get(PATH)
        commit('setSingle', data.data)
      } catch (e) {
        commit('setSingle', {})
      }
    },
    async add ({ commit }, params) {
      try {
        const { data } = await request.post(FUSEBOX.RESOURCE, params)
        return data.data.uuid
      } catch (e) {
        throw e
      }
    },
    async update (context, params) {
      try {
        await request.post(`${FUSEBOX.RESOURCE}/${params.uuid}`, params.form)
      } catch (e) {
        throw e
      }
    },
    async delete (context, id) {
      try {
        await request.delete(`${FUSEBOX.RESOURCE}/${id}`)
        return
      } catch (e) {
        throw e
      }
    },
    async initGroup ({ commit, getters }, params) {
      try {
        const PATH = params.public ? PUBLIC.INIT : FUSEBOX.GROUP.INIT
        const { data } = await request.get(`${bindVar(PATH, { fusebox: params.id })}`)
        commit('setFuseboxGroupList', data.fusebox_groups)
        return data
      } catch (e) {
        commit('setFuseboxGroupList', {})
      }
    },
    async addGroup ({ commit }, params) {
      try {
        const { data } = await request.post(`${bindVar(FUSEBOX.GROUP.RESOURCE, { fusebox: params.id })}`, params.form)
        commit('setFuseboxGroupList', data.group)
        commit('setHistory', data.activity)
        return data.group.id
      } catch (e) {
        throw e
      }
    },
    async updateGroup ({ commit }, params) {
      try {
        const { data } = await request.put(`${bindVar(FUSEBOX.GROUP.DELETE, {
          fusebox: params.id,
          fuseboxGroup: params.uuid
        })}`, params.form)
        commit('setFuseboxGroupList', data.group)
        commit('setHistory', data.activity)
        return data.group.id
      } catch (e) {
        throw e
      }
    },
    async deleteGroup ({ commit, getters }, params) {
      try {
        const { data } = await request.delete(`${bindVar(FUSEBOX.GROUP.DELETE, {
          fusebox: params.id,
          fuseboxGroup: params.group_id
        })}`)
        const newList = [...getters.fuseboxGroupList]
        newList.splice(params.index, 1)
        commit('setFuseboxGroupList', newList)
        commit('setHistory', data)
      } catch (e) {
        throw e
      }
    },
    async deleteImage ({ commit, getters }, { id, imageId, indexId }) {
      try {
        const newSingle = { ...getters.single }
        const newList = [...newSingle.images]

        await request.delete(`${FUSEBOX.IMAGES}/${id}/${imageId}`)

        newList.splice(indexId, 1)
        newSingle.images = newList
        commit('setSingle', newSingle)
      } catch (e) {
        throw e
      }
    },
    async pdfGenerate (context, qrCode) {
      try {
        const { data } = await request.get(bindVar(FUSEBOX.GENERATE, { qrCode }), {
          responseType: 'arraybuffer',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          timeout: 1000 * 300
        })
        return data
      } catch (e) {
        throw e
      }
    },
    async sendEmail ({ commit }, form) {
      try {
        await request.post(FUSEBOX.EMAIL, form, {
          timeout: 1000 * 300
        })
      } catch (e) {
        throw e
      }
    },
    async requestAccess ({ commit }, fusebox) {
      try {
        await request.post(bindVar(ACCESS.REQUEST, { fusebox }))
      } catch (e) {
        throw e
      }
    },
    async grantAccess ({ commit }, { fusebox, accessRequest, type }) {
      try {
        const { data } = await request.post(bindVar(ACCESS.GRANT, { fusebox, accessRequest, type }))
        commit('auth/setRequests', data.requests, { root: true })
        commit('auth/setManager', data.manager, { root: true })
      } catch (e) {
        throw e
      }
    },
    async revokeAccess ({ commit }, { fusebox }) {
      try {
        await request.post(bindVar(ACCESS.REVOKE, { fusebox }))
        commit('auth/setManager', false, { root: true })
      } catch (e) {
        throw e
      }
    }
  }
}
